import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHeading } from "@/components/Primitives";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Phone, Mail, MessageCircle, Check, Loader2, MapPin } from "lucide-react";

const CATS = ["Jewellery", "Clinic", "Restaurant", "Electronics", "Clothing", "Interior Design", "Service Business", "Other"];
const FEATURES = ["Product catalogue", "Online payments", "Appointment booking", "WhatsApp enquiries", "Blog", "Admin dashboard"];

export default function Contact() {
  const [sp] = useSearchParams();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", business_name: "", category: "", phone: "", email: "",
    features: [], budget_range: "", preferred_contact: "phone", consent: false,
    message: sp.get("service") || sp.get("ref") || sp.get("package") ? `Interested in: ${sp.get("service") || sp.get("ref") || sp.get("package")}` : "",
  });

  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const toggleFeature = (f) => setForm((p) => ({ ...p, features: p.features.includes(f) ? p.features.filter((x) => x !== f) : [...p.features, f] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please provide consent to be contacted.");
    setLoading(true);
    try {
      await api.post("/public/leads", { ...form, email: form.email || null, source: "contact_form", referral_code: localStorage.getItem("nova_ref") || null });
      setDone(true);
      toast.success("Enquiry sent successfully!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const fld = "bg-white/5 border-white/10 text-white placeholder:text-slate-500";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <SectionHeading eyebrow="Contact" title="Request your free demo"
        subtitle="Tell us about your business. We'll prepare a demo and get in touch through your preferred channel." />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 glass-strong rounded-3xl p-6 sm:p-8">
          {done ? (
            <div className="text-center py-16" data-testid="contact-success">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 mx-auto mb-5"><Check className="w-8 h-8" /></span>
              <h3 className="font-display text-2xl font-bold text-white mb-2">Thank you!</h3>
              <p className="text-slate-400">Your enquiry has been received. We'll reach out soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-200 mb-1.5 block">Name *</Label><Input required data-testid="contact-name" className={fld} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="text-slate-200 mb-1.5 block">Business name</Label><Input data-testid="contact-business" className={fld} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-200 mb-1.5 block">Business category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="contact-category" className={fld}><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10 text-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-200 mb-1.5 block">Phone *</Label><Input required data-testid="contact-phone" className={fld} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-200 mb-1.5 block">Email (optional)</Label><Input type="email" data-testid="contact-email" className={fld} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label className="text-slate-200 mb-1.5 block">Budget range (optional)</Label><Input data-testid="contact-budget" placeholder="e.g. ₹20,000 – ₹40,000" className={fld} value={form.budget_range} onChange={(e) => setForm({ ...form, budget_range: e.target.value })} /></div>
              </div>
              <div>
                <Label className="text-slate-200 mb-2 block">Required features</Label>
                <div className="flex flex-wrap gap-2">{FEATURES.map((f) => (
                  <button type="button" key={f} data-testid={`contact-feature-${f.replace(/\s/g,"-").toLowerCase()}`} onClick={() => toggleFeature(f)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.features.includes(f) ? "bg-violet-600 border-violet-500 text-white" : "border-white/10 text-slate-300 hover:border-violet-500/40"}`}>{f}</button>))}
                </div>
              </div>
              <div>
                <Label className="text-slate-200 mb-2 block">Preferred contact method</Label>
                <div className="flex gap-2">{["phone", "whatsapp", "email"].map((m) => (
                  <button type="button" key={m} onClick={() => setForm({ ...form, preferred_contact: m })}
                    className={`px-4 py-2 rounded-lg text-sm capitalize border ${form.preferred_contact === m ? "bg-violet-600 border-violet-500 text-white" : "border-white/10 text-slate-300"}`}>{m}</button>))}
                </div>
              </div>
              <div><Label className="text-slate-200 mb-1.5 block">Message</Label><Textarea data-testid="contact-message" className={fld} rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <label className="flex items-center gap-2.5 text-sm text-slate-300"><Checkbox checked={form.consent} onCheckedChange={(c) => setForm({ ...form, consent: !!c })} data-testid="contact-consent" /> I consent to be contacted by Nova Web Solutions about my enquiry. *</label>
              <Button type="submit" data-testid="contact-submit" disabled={loading} className="w-full h-12 bg-violet-600 hover:bg-violet-500 text-white font-semibold btn-glow">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Enquiry"}
              </Button>
            </form>
          )}
        </div>
        <div className="space-y-4">
          <div className="glass rounded-2xl p-6">
            <h4 className="font-semibold text-white mb-4">Reach us directly</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3 text-slate-300"><Phone className="w-4 h-4 text-violet-400" />{settings.phone || <span className="italic opacity-70">Phone to be configured</span>}</li>
              <li className="flex items-center gap-3 text-slate-300"><MessageCircle className="w-4 h-4 text-emerald-400" />{settings.whatsapp || <span className="italic opacity-70">WhatsApp to be configured</span>}</li>
              <li className="flex items-center gap-3 text-slate-300"><Mail className="w-4 h-4 text-cyan-400" />{settings.email || <span className="italic opacity-70">Email to be configured</span>}</li>
              <li className="flex items-start gap-3 text-slate-300"><MapPin className="w-4 h-4 text-violet-400 mt-0.5" />{settings.address || <span className="italic opacity-70">Address to be configured</span>}</li>
            </ul>
            {!settings.whatsapp_configured && <p className="text-xs text-amber-300/80 mt-4">Contact channels are not yet configured. We don't claim WhatsApp or email are live until verified.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
